// ═══════════════════════════════════════════════════════════════
// Handoff — Note Detail (Ported from original HandoffDetail.jsx)
// View, acknowledge, edit draft, print, AI summary, linked actions
// ═══════════════════════════════════════════════════════════════

import { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Title, Text, Paper, Button, Group, Stack, TextInput, Select,
  SimpleGrid, Badge, Alert, Center, Loader, ActionIcon,
} from '../../components/gab/GabComponents';
import {
  IconArrowLeft, IconCheck, IconPencil, IconPrinter,
  IconClock, IconUser, IconSun, IconMoon, IconAlertCircle,
  IconTrash, IconCircleCheck, IconShield, IconClipboardList,
  IconPlus, IconDeviceFloppy, IconSparkles, IconLoader,
  IconAlertTriangle, IconBolt, IconCircle,
} from '@tabler/icons-react';
import { useHandoffStore } from '../../store/handoffStore';
import { useAuthStore } from '../../store/authStore';
import { notifications } from '../../lib/notifications';
import Breadcrumbs from '../../components/ui/Breadcrumbs';

function formatTimestamp(ts: string | undefined | null): string {
  if (!ts) return '';
  try {
    return new Date(ts).toLocaleString();
  } catch {
    return String(ts);
  }
}

function relativeTime(ts: string | undefined | null): string {
  if (!ts) return '';
  try {
    const d = new Date(ts);
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const mins = Math.floor(diffMs / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    return `${days}d ago`;
  } catch {
    return '';
  }
}

export default function HandoffDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, incidents, loadIncidents } = useAuthStore();

  const {
    notes, actionItems, rosterUsers,
    loadNotes, loadActionItems, loadRosterUsers, acknowledgeNote, deleteNote, generateAISummary,
    createActionItem, completeActionItem, reopenActionItem, deleteActionItem,
    generateActionsFromHandoff,
  } = useHandoffStore();

  const [ackSignature, setAckSignature] = useState('');
  const [showAckForm, setShowAckForm] = useState(false);
  const [acknowledging, setAcknowledging] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [showActionForm, setShowActionForm] = useState(false);
  const [actionTitle, setActionTitle] = useState('');
  const [actionDeadline, setActionDeadline] = useState('');
  const [actionAssignee, setActionAssignee] = useState('');
  const [creatingSaving, setCreatingSaving] = useState(false);
  const [aiSummarizing, setAiSummarizing] = useState(false);
  const [aiActionsLoading, setAiActionsLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    Promise.all([loadIncidents(), loadNotes(), loadActionItems(), loadRosterUsers()])
      .then(() => setLoaded(true));
  }, []);

  const note = useMemo(() => notes.find(n => String(n.id) === id), [notes, id]);
  const event = useMemo(
    () => note ? incidents.find(e => String(e.id) === String(note.eventId)) : null,
    [note, incidents]
  );

  // Linked action items for this handoff note
  const linkedItems = useMemo(
    () => actionItems.filter(a => String(a.handoffNoteId) === id)
      .sort((a, b) => (b.datecreated || '').localeCompare(a.datecreated || '')),
    [actionItems, id]
  );

  // Permission checks
  const isCreator = note?.senderName === user?.fullName;
  const canEdit = note?.status === 'draft' && isCreator;
  const canDelete = note?.status === 'draft';
  const canAcknowledge = note?.status === 'submitted';

  const handleAcknowledge = async () => {
    if (!ackSignature.trim() || !note) return;
    setAcknowledging(true);
    try {
      await acknowledgeNote(note.id, user?.fullName ?? '', ackSignature.trim());
      notifications.show({ title: 'Success', message: 'Handoff acknowledged!', color: 'green' });
      setShowAckForm(false);
    } catch (err: any) {
      notifications.show({ title: 'Error', message: err?.message || 'Failed to acknowledge', color: 'red' });
    } finally {
      setAcknowledging(false);
    }
  };

  const handleDelete = async () => {
    if (!note) return;
    try {
      await deleteNote(note.id);
      notifications.show({ title: 'Deleted', message: 'Draft deleted', color: 'gray' });
      navigate(-1);
    } catch (err: any) {
      notifications.show({ title: 'Error', message: err?.message || 'Failed to delete', color: 'red' });
    }
  };

  const handleCreateActionItem = async () => {
    if (!actionTitle.trim() || !note) return;
    setCreatingSaving(true);
    try {
      await createActionItem({
        eventId: String(note.eventId),
        handoffNoteId: String(note.id),
        title: actionTitle,
        description: '',
        priority: 'normal',
        assignedTo: actionAssignee,
        dueDate: actionDeadline ? new Date(actionDeadline).toISOString() : '',
      });
      notifications.show({ title: 'Created', message: 'Action item created', color: 'green' });
      setActionTitle('');
      setActionDeadline('');
      setActionAssignee('');
      setShowActionForm(false);
    } catch (err: any) {
      notifications.show({ title: 'Error', message: err?.message || 'Failed to create', color: 'red' });
    } finally {
      setCreatingSaving(false);
    }
  };

  const handleSummarize = async () => {
    if (!note) return;
    setAiSummarizing(true);
    try {
      await generateAISummary(note);
      notifications.show({ title: 'Done', message: 'AI summary generated!', color: 'violet' });
    } catch (err: any) {
      notifications.show({ title: 'Error', message: err?.message || 'Failed to generate summary', color: 'red' });
    } finally {
      setAiSummarizing(false);
    }
  };

  const handleGenerateActions = async () => {
    if (!note) return;
    setAiActionsLoading(true);
    try {
      const raw = await generateActionsFromHandoff(note);
      let cleaned = (raw || '').trim();
      if (cleaned.startsWith('```')) cleaned = cleaned.replace(/^```json?\n?/, '').replace(/```$/, '');
      const items: { title: string; description?: string; assignedToName?: string; assignee?: string; deadline?: string; priority?: string }[] = JSON.parse(cleaned);
      let created = 0;
      for (const item of items) {
        await createActionItem({
          eventId: String(note.eventId),
          handoffNoteId: String(note.id),
          title: item.title,
          description: item.description || '',
          priority: (item.priority as any) || 'normal',
          assignedTo: item.assignedToName || item.assignee || '',
          dueDate: item.deadline || '',
        });
        created++;
      }
      notifications.show({ title: 'AI Actions', message: `Created ${created} action items from handoff`, color: 'violet' });
    } catch (err: any) {
      notifications.show({ title: 'Error', message: err?.message || 'Failed to generate actions', color: 'red' });
    } finally {
      setAiActionsLoading(false);
    }
  };

  // Loading state
  if (!loaded && !note) {
    return (
      <Center py="xl" mih={200}>
        <Stack align="center">
          <Loader />
          <Text c="dimmed">Loading handoff note...</Text>
        </Stack>
      </Center>
    );
  }

  if (!note) {
    return (
      <Center py="xl" mih={300}>
        <Stack align="center">
          <Title order={3}>Handoff note not found</Title>
          <Button leftSection={<IconArrowLeft size={16} />} onClick={() => navigate(-1)}>
            Go Back
          </Button>
        </Stack>
      </Center>
    );
  }

  const statusConfig: Record<string, { color: string; label: string }> = {
    draft: { color: 'gray', label: 'Draft' },
    submitted: { color: 'orange', label: 'Submitted' },
    acknowledged: { color: 'green', label: 'Acknowledged' },
  };
  const status = statusConfig[note.status] || statusConfig.draft;

  // Parse AI summary
  let aiParsed: { summary: string; keyPoints: string[]; urgentItems: string[] } | null = null;
  if (note.aiSummary) {
    try {
      let cleaned = note.aiSummary.trim();
      if (cleaned.startsWith('```')) cleaned = cleaned.replace(/^```json?\n?/, '').replace(/```$/, '');
      aiParsed = JSON.parse(cleaned);
    } catch {
      // Will render raw text
    }
  }

  return (
    <Box>
      <Breadcrumbs items={[
        { label: 'Hub', to: '/' },
        { label: 'Handoff', to: '/handoff' },
        { label: 'Handoffs', to: '/handoff/notes' },
        { label: note?.title || 'Handoff' },
      ]} />
      {/* Back */}
      <Button
        variant="subtle"
        size="compact-sm"
        leftSection={<IconArrowLeft size={14} />}
        onClick={() => {
          if (event) {
            navigate(`/handoff/event/${event.id}`);
          } else {
            navigate('/handoff/notes');
          }
        }}
        mb="sm"
        className="no-print"
      >
        {event ? 'Back to Event' : 'Back'}
      </Button>

      {/* Header */}
      <Group justify="space-between" align="flex-start" mb="lg" className="no-print">
        <Box>
          <Group gap="xs" mb={4}>
            <Badge
              color={status.color}
              variant="light"
              size="lg"
            >
              {status.label}
            </Badge>
            {note.fromShift && (
              <Badge variant="light" color="blue">
                {note.fromShift === 'day'
                  ? <IconSun size={12} style={{ marginRight: 4 }} />
                  : <IconMoon size={12} style={{ marginRight: 4 }} />}
                {note.fromShift === 'day' ? 'Day Shift' : 'Night Shift'}
              </Badge>
            )}
            {note.positionName && (
              <Badge variant="outline">{note.positionName}</Badge>
            )}
          </Group>
          <Title order={2}>
            {note.positionName ? `Handoff: ${note.positionName}` : 'Handoff Note'}
          </Title>
          <Group gap="md" mt="xs">
            <Text size="sm" c="dimmed">
              <IconUser size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />
              From: {note.senderName}
            </Text>
            {note.recipientName && (
              <Text size="sm" c="dimmed">
                <IconUser size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                To: {note.recipientName}
              </Text>
            )}
            {note.fromShift && (
              <Text size="sm" c="dimmed">
                {note.fromShift === 'day'
                  ? <IconSun size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                  : <IconMoon size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />}
                {note.fromShift} &rarr; {note.toShift || 'next'} shift
              </Text>
            )}
            {note.datecreated && (
              <Text size="sm" c="dimmed">
                <IconClock size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                {relativeTime(note.datecreated)}
              </Text>
            )}
          </Group>
        </Box>

        <Group gap="xs">
          {canEdit && (
            <Button
              variant="default"
              size="compact-sm"
              leftSection={<IconPencil size={14} />}
              onClick={() => navigate(`/handoff/new?draft=${note.id}`)}
            >
              Edit
            </Button>
          )}
          {canDelete && !confirmDelete && (
            <Button
              variant="default"
              size="compact-sm"
              leftSection={<IconTrash size={14} />}
              onClick={() => setConfirmDelete(true)}
            >
              Delete
            </Button>
          )}
          {confirmDelete && (
            <Group gap="xs">
              <Button color="red" size="compact-sm" onClick={handleDelete}>Confirm Delete</Button>
              <Button variant="subtle" size="compact-sm" onClick={() => setConfirmDelete(false)}>Cancel</Button>
            </Group>
          )}
          <Button
            variant="default"
            size="compact-sm"
            leftSection={<IconPrinter size={14} />}
            onClick={() => window.print()}
          >
            Print
          </Button>
        </Group>
      </Group>

      {/* Print header (only visible when printing) */}
      <div className="print-only print-header" style={{ display: 'none' }}>
        <h1>HANDOFF NOTE — {note.positionName || 'General'}</h1>
        <div className="print-meta">
          <span>Status: {status.label}</span>
          <span>Shift: {note.fromShift === 'day' ? 'Day' : 'Night'}</span>
          <span>From: {note.senderName}</span>
          {note.recipientName && <span>To: {note.recipientName}</span>}
          <span>Date: {formatTimestamp(note.datecreated)}</span>
        </div>
      </div>

      {/* Timeline */}
      <Paper p="md" withBorder mb="md">
        <Stack gap="xs">
          <Group gap="sm">
            <Box
              style={{
                width: 10, height: 10, borderRadius: '50%',
                backgroundColor: 'var(--mantine-color-blue-6)', flexShrink: 0,
              }}
            />
            <Text size="sm" fw={500}>Created</Text>
            <Text size="xs" c="dimmed">{formatTimestamp(note.datecreated)}</Text>
          </Group>
          {note.submittedAt && (
            <Group gap="sm">
              <Box
                style={{
                  width: 10, height: 10, borderRadius: '50%',
                  backgroundColor: 'var(--mantine-color-orange-6)', flexShrink: 0,
                }}
              />
              <Text size="sm" fw={500}>Submitted</Text>
              <Text size="xs" c="dimmed">{formatTimestamp(note.submittedAt)}</Text>
            </Group>
          )}
          {note.acknowledgedAt && (
            <Group gap="sm">
              <Box
                style={{
                  width: 10, height: 10, borderRadius: '50%',
                  backgroundColor: 'var(--mantine-color-green-6)', flexShrink: 0,
                }}
              />
              <Text size="sm" fw={500}>Acknowledged</Text>
              <Text size="xs" c="dimmed">{formatTimestamp(note.acknowledgedAt)}</Text>
            </Group>
          )}
        </Stack>
      </Paper>

      {/* Sections */}
      <Paper p="md" withBorder mb="md">
        {note.sections && note.sections.length > 0 ? (
          <Stack gap="md">
            {note.sections.map((item, index) => (
              <Box key={index}>
                <Title order={4} mb="xs">{item.title || `Section ${index + 1}`}</Title>
                <Box>
                  {item.content ? (
                    item.content.split('\n').map((line, i) => (
                      <Text key={i} size="sm" mb={2}>{line || '\u00A0'}</Text>
                    ))
                  ) : (
                    <Text size="sm" c="dimmed">No content</Text>
                  )}
                </Box>
                {index < note.sections.length - 1 && (
                  <Box mt="md" style={{ borderBottom: '1px solid var(--mantine-color-gray-3)' }} />
                )}
              </Box>
            ))}
          </Stack>
        ) : (
          <Text c="dimmed" ta="center" py="md">No sections in this handoff note.</Text>
        )}
      </Paper>

      {/* AI Summary */}
      {note.status !== 'draft' && (
        <Paper
          p="md"
          withBorder
          mb="md"
          className="no-print"
          style={{ borderLeftWidth: 3, borderLeftColor: 'var(--mantine-color-violet-5)' }}
        >
          <Group justify="space-between" mb="sm">
            <Group gap="xs">
              <IconSparkles size={16} style={{ color: 'var(--mantine-color-violet-5)' }} />
              <Title order={4}>AI Summary</Title>
            </Group>
            <Button
              variant="subtle"
              size="compact-sm"
              leftSection={aiSummarizing ? <IconLoader size={14} className="spin" /> : <IconSparkles size={14} />}
              onClick={handleSummarize}
              disabled={aiSummarizing}
            >
              {aiSummarizing
                ? 'Analyzing...'
                : note.aiSummary
                  ? 'Regenerate'
                  : 'Generate Summary'}
            </Button>
          </Group>

          {aiSummarizing && (
            <Center py="md">
              <Stack align="center" gap="xs">
                <Loader size="sm" />
                <Text size="sm" c="dimmed">Analyzing handoff content with AI...</Text>
              </Stack>
            </Center>
          )}

          {note.aiSummary && !aiSummarizing && (
            <Stack gap="md">
              {aiParsed ? (
                <>
                  <Text size="sm">{aiParsed.summary}</Text>

                  {aiParsed.keyPoints && aiParsed.keyPoints.length > 0 && (
                    <Box>
                      <Group gap="xs" mb="xs">
                        <IconBolt size={14} />
                        <Text size="sm" fw={600}>Key Points</Text>
                      </Group>
                      <Stack gap={4}>
                        {aiParsed.keyPoints.map((point, i) => (
                          <Text key={i} size="sm" pl="md">&bull; {point}</Text>
                        ))}
                      </Stack>
                    </Box>
                  )}

                  {aiParsed.urgentItems && aiParsed.urgentItems.length > 0 && (
                    <Alert color="red" variant="light" icon={<IconAlertTriangle size={14} />}>
                      <Text size="sm" fw={600} mb="xs">Urgent Items</Text>
                      <Stack gap={4}>
                        {aiParsed.urgentItems.map((item, i) => (
                          <Text key={i} size="sm">&bull; {item}</Text>
                        ))}
                      </Stack>
                    </Alert>
                  )}
                </>
              ) : (
                <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>{note.aiSummary}</Text>
              )}
            </Stack>
          )}

          {!note.aiSummary && !aiSummarizing && (
            <Text size="sm" c="dimmed" ta="center" py="md">
              Click "Generate Summary" to create an AI-powered summary of this handoff note.
            </Text>
          )}
        </Paper>
      )}

      {/* Signatures */}
      <Paper p="md" withBorder mb="md">
        <Group gap="xs" mb="md">
          <IconShield size={16} />
          <Title order={4}>Signatures</Title>
        </Group>
        <SimpleGrid cols={2}>
          <Paper p="sm" withBorder>
            <Text size="xs" c="dimmed" mb={4}>Outgoing (Sender)</Text>
            {note.senderSignature ? (
              <Text size="sm" fw={500} fs="italic">{note.senderSignature}</Text>
            ) : (
              <Text size="sm" c="dimmed">Not signed</Text>
            )}
            <Text size="xs" c="dimmed" mt={4}>{note.senderName}</Text>
          </Paper>
          <Paper p="sm" withBorder>
            <Text size="xs" c="dimmed" mb={4}>Incoming (Recipient)</Text>
            {note.recipientSignature ? (
              <Text size="sm" fw={500} fs="italic">{note.recipientSignature}</Text>
            ) : (
              <Text size="sm" c="dimmed">Awaiting acknowledgment</Text>
            )}
            <Text size="xs" c="dimmed" mt={4}>{note.recipientName || '\u2014'}</Text>
          </Paper>
        </SimpleGrid>
      </Paper>

      {/* Acknowledge form */}
      {canAcknowledge && !showAckForm && (
        <Alert
          color="yellow"
          variant="light"
          icon={<IconAlertCircle size={20} />}
          mb="md"
          className="no-print"
        >
          <Group justify="space-between" align="center">
            <Box>
              <Text fw={600}>This handoff requires your acknowledgment.</Text>
              <Text size="sm">Review the information above, then acknowledge to complete the handoff.</Text>
            </Box>
            <Button leftSection={<IconCheck size={14} />} onClick={() => setShowAckForm(true)}>
              Acknowledge
            </Button>
          </Group>
        </Alert>
      )}

      {showAckForm && (
        <Paper p="md" withBorder mb="md" className="no-print">
          <Title order={4} mb="sm">Acknowledge Handoff</Title>
          <TextInput
            label="Your Signature"
            value={ackSignature}
            onChange={(e) => setAckSignature(e.target.value)}
            placeholder="Type your name as signature"
            autoFocus
            mb="sm"
          />
          <Group gap="xs">
            <Button variant="default" onClick={() => setShowAckForm(false)}>
              Cancel
            </Button>
            <Button
              leftSection={<IconCheck size={14} />}
              onClick={handleAcknowledge}
              disabled={!ackSignature.trim() || acknowledging}
            >
              {acknowledging ? 'Acknowledging...' : 'Confirm Acknowledge'}
            </Button>
          </Group>
        </Paper>
      )}

      {/* Action Items linked to this Handoff */}
      {note.status !== 'draft' && (
        <Paper p="md" withBorder mb="md" className="no-print">
          <Group justify="space-between" mb="sm">
            <Group gap="xs">
              <IconClipboardList size={16} />
              <Title order={4}>Action Items</Title>
              {linkedItems.length > 0 && (
                <Badge size="sm" variant="light">{linkedItems.length}</Badge>
              )}
            </Group>
            {!showActionForm && (
              <Group gap="xs">
                <Button
                  variant="subtle"
                  size="compact-sm"
                  leftSection={<IconSparkles size={14} />}
                  onClick={handleGenerateActions}
                  loading={aiActionsLoading}
                  color="violet"
                >
                  AI Generate Actions
                </Button>
                <Button
                  variant="subtle"
                  size="compact-sm"
                  leftSection={<IconPlus size={14} />}
                  onClick={() => setShowActionForm(true)}
                >
                  Create from Handoff
                </Button>
              </Group>
            )}
          </Group>

          {/* List of linked action items */}
          {linkedItems.length > 0 && (
            <Stack gap="xs" mb={showActionForm ? 'sm' : 0}>
              {linkedItems.map((item) => {
                const isCompleted = item.status === 'completed';
                const isOverdue = item.dueDate && item.status === 'pending' &&
                  new Date(item.dueDate) < new Date();
                const bgColor = isCompleted
                  ? 'var(--mantine-color-green-0)'
                  : isOverdue
                    ? 'var(--mantine-color-red-0)'
                    : 'var(--mantine-color-gray-0)';
                return (
                  <Group
                    key={item.id}
                    gap="sm"
                    justify="space-between"
                    p="xs"
                    style={{ borderRadius: 'var(--mantine-radius-sm)', background: bgColor }}
                  >
                    <Group gap="sm" style={{ flex: 1, minWidth: 0 }}>
                      {isCompleted ? (
                        <IconCircleCheck size={16} style={{ color: 'var(--mantine-color-green-6)', flexShrink: 0 }} />
                      ) : isOverdue ? (
                        <IconAlertTriangle size={16} style={{ color: 'var(--mantine-color-red-6)', flexShrink: 0 }} />
                      ) : (
                        <IconCircle size={16} style={{ color: 'var(--mantine-color-blue-5)', flexShrink: 0 }} />
                      )}
                      <Box style={{ minWidth: 0 }}>
                        <Text
                          size="sm"
                          fw={500}
                          td={isCompleted ? 'line-through' : undefined}
                          truncate
                        >
                          {item.title}
                        </Text>
                        <Group gap="xs">
                          {item.assignedTo && (
                            <Text size="xs" c="dimmed">{item.assignedTo}</Text>
                          )}
                          {item.dueDate && (
                            <Text size="xs" c={isOverdue ? 'red' : 'dimmed'}>
                              Due: {new Date(item.dueDate).toLocaleDateString()}
                            </Text>
                          )}
                        </Group>
                      </Box>
                    </Group>
                    <Group gap="xs">
                      <Button
                        variant="subtle"
                        size="compact-xs"
                        color={isCompleted ? 'blue' : 'green'}
                        onClick={async () => {
                          try {
                            if (isCompleted) {
                              await reopenActionItem(item.id);
                              notifications.show({ message: 'Action item reopened', color: 'blue' });
                            } else {
                              await completeActionItem(item.id);
                              notifications.show({ message: 'Action item completed', color: 'green' });
                            }
                          } catch (err: any) {
                            notifications.show({ message: err?.message || 'Failed to update', color: 'red' });
                          }
                        }}
                      >
                        {isCompleted ? 'Reopen' : 'Complete'}
                      </Button>
                      <ActionIcon
                        variant="subtle"
                        color="red"
                        size="sm"
                        onClick={async () => {
                          try {
                            await deleteActionItem(item.id);
                            notifications.show({ message: 'Action item deleted', color: 'gray' });
                          } catch (err: any) {
                            notifications.show({ message: err?.message || 'Failed to delete', color: 'red' });
                          }
                        }}
                      >
                        <IconTrash size={12} />
                      </ActionIcon>
                    </Group>
                  </Group>
                );
              })}
            </Stack>
          )}

          {linkedItems.length === 0 && !showActionForm && (
            <Text size="sm" c="dimmed" ta="center" py="xs">
              No action items linked to this handoff.
            </Text>
          )}

          {/* Create form */}
          {showActionForm && (
            <Box>
              <TextInput
                label="Action Item Title"
                value={actionTitle}
                onChange={(e) => setActionTitle(e.target.value)}
                placeholder="What needs to be done?"
                autoFocus
                mb="sm"
              />
              <SimpleGrid cols={{ base: 1, sm: 2 }} mb="sm">
                <Select
                  label="Assign To"
                  placeholder="Select person..."
                  data={rosterUsers.map(u => ({ value: u.fullName, label: u.fullName }))}
                  value={actionAssignee || null}
                  onChange={(v) => setActionAssignee(v ?? '')}
                  searchable clearable
                />
                <Box>
                  <Text size="sm" fw={500} mb={4}>Deadline</Text>
                  <input
                    type="datetime-local"
                    value={actionDeadline}
                    onChange={(e) => setActionDeadline(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '7px 12px',
                      border: '1px solid var(--mantine-color-gray-4)',
                      borderRadius: 'var(--mantine-radius-sm)',
                      fontSize: 'var(--mantine-font-size-sm)',
                    }}
                  />
                </Box>
              </SimpleGrid>
              <Group gap="xs">
                <Button variant="default" onClick={() => setShowActionForm(false)}>Cancel</Button>
                <Button
                  leftSection={<IconDeviceFloppy size={14} />}
                  onClick={handleCreateActionItem}
                  disabled={creatingSaving || !actionTitle.trim()}
                >
                  {creatingSaving ? 'Creating...' : 'Create Action Item'}
                </Button>
              </Group>
            </Box>
          )}
        </Paper>
      )}
    </Box>
  );
}
